import ArrowBackIos from '@mui/icons-material/ArrowBackIos';
import Button from '@mui/material/Button';
import { type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { getColors } from '../tokens/getColors';
import { fontFamilyCorpoSText } from '../tokens/typography';
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from './ODPSidebar';
import { useSidebar } from './SidebarContext';
import { UserMenu } from './UserMenu';

export const HEADER_HEIGHT = 56;

interface ODPHeaderProps {
  transparent?: boolean;
  actions?: ReactNode;
}

const useStyles = makeStyles<{ transparent: boolean; collapsed: boolean }>()((
  theme,
  { transparent, collapsed },
) => {
  const c = getColors(theme.palette.mode);
  return {
    header: {
      position: 'sticky' as const,
      top: 0,
      left: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
      right: 0,
      height: HEADER_HEIGHT,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 24px',
      backgroundColor: transparent ? 'transparent' : c.background.sidebar,
      borderBottom: transparent ? 'none' : `1px solid ${c.border.default}`,
      zIndex: 1100,
      fontFamily: fontFamilyCorpoSText,
    },
    leftSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    backButton: {
      color: c.text.primary,
      textTransform: 'none',
      fontFamily: fontFamilyCorpoSText,
      fontSize: '0.875rem',
      padding: '4px 8px',
      borderRadius: '6px',
      '&:hover': {
        color: c.primary.main,
      },
    },
    rightSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    iconButton: {
      background: 'none',
      border: 'none',
      color: c.text.secondary,
      padding: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'default',
      borderRadius: '6px',
      position: 'relative' as const,
    },
    badge: {
      position: 'absolute' as const,
      top: '0px',
      right: '0px',
      minWidth: '16px',
      height: '16px',
      borderRadius: '8px',
      backgroundColor: '#00C853',
      color: '#FFFFFF',
      fontSize: '9px',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 3px',
    },
  };
});

export const ODPHeader = ({
  transparent = false,
  actions,
}: Readonly<ODPHeaderProps>) => {
  const { collapsed } = useSidebar();
  const { classes } = useStyles({ transparent, collapsed });
  const theme = useTheme();
  const colors = getColors(theme.palette.mode);
  const navigate = useNavigate();
  const location = useLocation();
  const hasInternalHistory = location.key !== 'default';

  const handleBack = () => {
    if (hasInternalHistory) {
      navigate(-1);
    }
  };

  return (
    <header className={classes.header}>
      <div className={classes.leftSection}>
        {hasInternalHistory && (
          <Button
            type="button"
            className={classes.backButton}
            onClick={handleBack}
            size="small"
            startIcon={<ArrowBackIos fontSize="small" />}
          >
            Back
          </Button>
        )}
      </div>

      <div className={classes.rightSection}>
        {actions && (
          <div
            style={{
              color: colors.text.primary,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {actions}
          </div>
        )}
        <UserMenu />
      </div>
    </header>
  );
};
